import { useCallback, useState } from 'react'
import { supabase } from '../lib/supabase'

/**
 * Hook fino sobre a Edge Function `ai-insights`. Não cacheia entre chamadas
 * — caller controla o estado da resposta. Use casos:
 *   - dashboard         → resumo executivo do estoque a partir de AnalyticsKpis
 *   - purchase_item     → "por que comprar" de 1 item do PurchaseSuggestionPage
 *   - purchase_supplier → análise consolidada de um pacote de fornecedor
 *
 * O backend só aceita objetos JÁ AGREGADOS — não passe linhas brutas do DB.
 */
export type AiKind = 'dashboard' | 'purchase_item' | 'purchase_supplier' | 'miguel_comprador'

export interface MiguelTrendRecommendation {
  sku: string
  name: string
  tag: string
  recomendacao: string
  acao: string
  /** Sprint 3: presente em itens "em alta" quando o cap aperta. */
  quanto_cabe?: { qty: number; valor: number; mes_caixa: string } | null
}

/** Sprint 3: plano de caixa devolvido quando há orçamento cadastrado. */
export interface MiguelPlanoCaixa {
  mes_atual: { teto: number; consumido: number; disponivel: number }
  mes_seguinte_projecao?: number | null
  alertas?: string[]
}

export interface MiguelCompradorResponse {
  resumo: string
  subindo: MiguelTrendRecommendation[]
  caindo: MiguelTrendRecommendation[]
  plano_caixa?: MiguelPlanoCaixa | null
}

interface InvokeResult {
  ok: boolean
  /** Texto puro (kinds: dashboard, purchase_item; ou fallback do purchase_supplier). */
  text?: string
  /** Objeto JSON estruturado (kind: purchase_supplier). */
  data?: unknown
  error?: string
}

export interface SupplierAnalysis {
  veredito: 'aprovar' | 'aprovar_com_cortes' | 'postergar' | 'completar_pedido'
  resumo: string
  destaques: Array<{ sku: string; name: string; tag: 'URGENTE' | 'REPOR' | 'OPORTUNIDADE'; razao: string }>
  cortes: Array<{ sku: string; name: string; razao: string }>
  /** Status da compra mínima (calculado no servidor, não no LLM). */
  compra_minima?: {
    minimo: number | null
    valor_atual: number
    atingida: boolean
    gap: number
    percentual: number | null
  }
  /** Itens sugeridos pra fechar compra mínima — greedy A→B→C, sem encalhe. */
  composicao_extra?: Array<{
    sku: string
    name: string
    curva: 'A' | 'B' | 'C' | '?'
    /** Qty TOTAL final (substitui qty_atual, não soma). */
    qty: number
    /** Qty original sugerida pelo motor de reposição (pra contraste visual). */
    qty_sugerida: number
    valor: number
    razao: string
    /** True quando qty < qty_sugerida — UI mostra "qty ajustada pra não passar do mínimo". */
    ajustada: boolean
    /** 'novo' = SKU não estava no carrinho; 'aumento_carrinho' = item já no carrinho com qty aumentada. */
    origem: 'novo' | 'aumento_carrinho'
    /** Só quando origem='aumento_carrinho' — qty anterior, pra UI mostrar "5 → 12". */
    qty_atual?: number
  }>
  /** Prazos de pagamento cadastrados com vencimento calculado. */
  prazos_recomendados?: Array<{
    dias: number
    vencimento_iso: string
    vencimento_label: string
    mes_seguinte: boolean
  }>
  /** True se hoje >= dia de corte (default 25). UI só mostra prazos se true. */
  apos_dia_25?: boolean
}

export function useAiInsights() {
  const [loading, setLoading] = useState(false)

  const generate = useCallback(
    async (kind: AiKind, payload: Record<string, unknown>): Promise<InvokeResult> => {
      setLoading(true)
      try {
        const { data, error } = await supabase.functions.invoke<InvokeResult>(
          'ai-insights',
          { body: { kind, payload } }
        )
        if (data?.ok && (data.text || data.data)) return { ok: true, text: data.text, data: data.data }
        if (data?.error) return { ok: false, error: data.error }

        // supabase-js levanta FunctionsHttpError em 4xx/5xx; o body real da resposta
        // pode estar em error.context.response (Response) ou em error.context.json (helper).
        // Tentamos os dois antes de cair no message genérico.
        interface SbErrCtx {
          context?: {
            json?: () => Promise<unknown>
            response?: Response
            status?: number
          }
        }
        const ctx = (error as SbErrCtx | undefined)?.context

        if (ctx?.json) {
          try {
            const body = await ctx.json() as { error?: string } | undefined
            if (body?.error) return { ok: false, error: body.error }
          } catch { /* noop */ }
        }
        if (ctx?.response) {
          try {
            const body = await ctx.response.clone().json() as { error?: string } | undefined
            if (body?.error) return { ok: false, error: body.error }
          } catch {
            try {
              const txt = (await ctx.response.clone().text()).slice(0, 200)
              if (txt) return { ok: false, error: `IA (HTTP ${ctx.response.status}): ${txt}` }
            } catch { /* noop */ }
          }
        }
        // Loga no console pra debug ficar fácil quando o usuário reporta erro vago.
        // eslint-disable-next-line no-console
        console.error('[useAiInsights] invoke falhou sem body parseável', { error, errorContext: ctx, kind })
        return {
          ok: false,
          error: error?.message?.trim() || (ctx?.status ? `IA falhou (HTTP ${ctx.status}).` : 'Falha ao chamar IA — veja console (F12) pra detalhes.'),
        }
      } finally {
        setLoading(false)
      }
    },
    []
  )

  return { generate, loading }
}
